<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import EventLogPanel from '../components/EventLogPanel.vue'
import ExportDialog from '../components/ExportDialog.vue'
import ProfilePickerDialog from '../components/ProfilePickerDialog.vue'
import TopBar from '../components/TopBar.vue'
import { DEFAULT_DOCUMENT_NAME } from '../branding'
import { SELECTED_PROFILE_KEY, useAuth } from '../composables/useAuth'
import { useDocumentIO } from '../composables/useDocumentIO'
import { licenseFailureMessage, useLicense } from '../composables/useLicense'
import { useMyProfiles } from '../composables/useMyProfiles'
import type { CapabilityProfile } from '../types/callables'
import Shell from '../ui/components/Shell.vue'
import { openDialog } from '../ui/dialogs'
import { attach as attachEventLog } from '../ui/eventLog'
import { toast } from '../ui/toasts'
import type {
  ExportImageOptions,
  SketchElement,
  SketchErrorDetail,
  SketchLicenseValidatedDetail,
} from '../types/sketcher'

const sketch = ref<SketchElement | null>(null)
const router = useRouter()

const { user, logout } = useAuth()
const { fetchLicense, failure } = useLicense()
const { load: loadMyProfiles } = useMyProfiles()
const io = useDocumentIO(() => sketch.value)

const banner  = ref<string | null>(null)
const fatal   = ref(false)
const booting = ref(true)

// Load-time capability warnings. The engine emits one `sketch-error` with code
// DOCUMENT_CAPABILITY_RESTRICTED per active restrictive capability that has
// matching content in the loaded document. Each becomes a warn toast deduped
// by capabilityId so a fresh load of the same capability replaces its
// predecessor rather than stacking.
const CAPABILITY_TOAST_MS = 9000

// Profile state — populated during boot and reused when the user opens the
// "Switch profile" menu item. `defaultProfile` mirrors the user doc field, only
// used to pre-select the picker radio.
const myProfiles     = ref<CapabilityProfile[]>([])
const defaultProfile = ref<string | null>(null)

let detach: (() => void) | null = null
let detachEventLog: (() => void) | null = null

function formatCapabilityWarning(capabilityId: string, detail: Record<string, unknown>): string {
  // Engine payload shape: entity-typed caps carry { entityCount, entityTypes? };
  // layers.disabled carries { layerCount }; pages.disabled carries { pageCount }.
  // callout.disabled never fires this signal (no dedicated entity type).
  const entityCount = typeof detail.entityCount === 'number' ? detail.entityCount : null
  const layerCount  = typeof detail.layerCount  === 'number' ? detail.layerCount  : null
  const pageCount   = typeof detail.pageCount   === 'number' ? detail.pageCount   : null
  const types       = Array.isArray(detail.entityTypes)
    ? (detail.entityTypes as unknown[]).filter((t): t is string => typeof t === 'string')
    : []

  if (layerCount !== null) {
    return `Layer management is disabled — ${layerCount} layer${layerCount === 1 ? '' : 's'} in the document are read-only.`
  }
  if (pageCount !== null) {
    return `Page management is disabled — ${pageCount} page${pageCount === 1 ? '' : 's'} in the document are read-only.`
  }
  if (entityCount !== null) {
    const label = types.length === 1 ? types[0] : capabilityId.replace(/\..*$/, '')
    const plural = entityCount === 1 ? '' : 's'
    return `Document contains ${entityCount} ${label} entit${entityCount === 1 ? 'y' : 'ies'}${plural} that cannot be edited in this configuration.`
  }
  return `Content restricted by capability ${capabilityId}.`
}

function onSketchError(e: Event) {
  const detail = (e as CustomEvent<SketchErrorDetail>).detail
  if (!detail) return

  // Only the load-time capability signal is handled here. Other engine errors
  // (COMMAND_FAILED, INTEGRITY_ERROR, etc.) are surfaced through useDocumentIO
  // or ignored — routing them all through this toast would be noisy.
  //
  // String-matched because DOCUMENT_CAPABILITY_RESTRICTED is not yet listed in
  // the engine's ENGINE_ERROR map declared in Sketcher.d.ts (engine-side gap).
  if ((detail.code as string) !== 'DOCUMENT_CAPABILITY_RESTRICTED') return

  const payload = (detail.detail ?? {}) as Record<string, unknown>
  const capabilityId = typeof payload.capabilityId === 'string' ? payload.capabilityId : null
  if (!capabilityId) return

  toast.warn(formatCapabilityWarning(capabilityId, payload), {
    dedupeKey: capabilityId,
    timeoutMs: CAPABILITY_TOAST_MS,
  })
}

function onLicenseValidated(e: Event) {
  const detail = (e as CustomEvent<SketchLicenseValidatedDetail>).detail
  if (detail.status === 'valid') return

  // The engine accepted the token structurally but downgraded it. Nothing the
  // user can do — surface it rather than leaving a silently crippled canvas.
  banner.value =
    detail.status === 'view-only'
      ? 'Your licence has expired — the drawing is read-only. Reload to renew.'
      : 'Licence invalid — contact support.'
  fatal.value = detail.status === 'revoked'
}

/**
 * Decide which profile id to pass to generateLicense given the assignment
 * list, the user's default, and any cached choice from a prior boot in the
 * same tab. Returns:
 *   - a string id → send it as the profileId
 *   - null        → user has no profiles, unrestricted licence
 *   - 'need-pick' → 2+ profiles and no valid cached choice; caller must open
 *                   the picker and await selection before continuing
 */
function resolveProfileId(
  profiles: CapabilityProfile[],
  cached:   string | null,
): string | null | 'need-pick' {
  if (profiles.length === 0) return null
  if (profiles.length === 1) return profiles[0]!.id

  // 2+ profiles: use cached only if it's still in the assignment list.
  // Stale cache (admin removed the profile since last boot) falls through to
  // a fresh pick rather than letting generateLicense reject.
  if (cached && profiles.some((p) => p.id === cached)) return cached
  return 'need-pick'
}

async function initializeEngine(license: string) {
  const el = sketch.value
  if (!el) return
  try {
    el.initialize()
    await el.setLicense({ license })
    await el.newDocument({ name: DEFAULT_DOCUMENT_NAME })
    detach = io.attach(el)
  } catch {
    banner.value = 'Could not start Sketcher. Reload to try again.'
    fatal.value  = true
  } finally {
    booting.value = false
  }
}

async function fetchAndInitialize(profileId: string | null) {
  const license = await fetchLicense(profileId ?? undefined)
  if (!license) {
    // Stale cache: recover by clearing and reloading — the fresh boot re-fetches
    // profiles and either auto-selects, prompts, or issues unrestricted.
    if (failure.value === 'profile-not-assigned' || failure.value === 'profile-not-found') {
      try { sessionStorage.removeItem(SELECTED_PROFILE_KEY) } catch { /* ignore */ }
      banner.value = licenseFailureMessage(failure.value)
      fatal.value  = true
      booting.value = false
      return
    }

    banner.value = licenseFailureMessage(failure.value ?? 'unknown')
    fatal.value  = true
    booting.value = false

    // A deactivated account should not sit on a dead /app screen looking signed
    // in. Drop them back to the landing page with an explanation.
    if (failure.value === 'account-inactive' || failure.value === 'unauthenticated') {
      await logout()
      await router.replace({ name: 'landing', query: { error: 'account-inactive' } })
    }
    return
  }

  await initializeEngine(license)
}

/**
 * Open the picker and wire the picked profile back through either the boot
 * flow (fetch license + initialise the engine, with the dialog holding
 * "Loading…" until initialisation returns) or the switch flow (hard reload
 * — simpler and more correct than tearing down <cad-sketch>).
 */
function requestProfilePick(profiles: CapabilityProfile[], required: boolean) {
  void openDialog<string>(ProfilePickerDialog, {
    profiles,
    defaultProfile: defaultProfile.value,
    required,
    onSelect: async (profileId: string) => {
      try { sessionStorage.setItem(SELECTED_PROFILE_KEY, profileId) } catch { /* ignore */ }
      if (required) {
        await fetchAndInitialize(profileId)
      } else {
        // Reload is imminent; we don't need to await anything — the dialog
        // will unmount along with everything else when the tab reloads.
        location.reload()
      }
    },
  }, { dismissible: !required })
}

function onSwitchProfile() {
  requestProfilePick(myProfiles.value, false)
}

async function onExport() {
  const options = await openDialog<ExportImageOptions>(ExportDialog)
  if (!options) return
  await io.exportImage(options)
}

onMounted(async () => {
  const el = sketch.value
  if (!el) return
  el.addEventListener('sketch-license-validated', onLicenseValidated)
  el.addEventListener('sketch-error', onSketchError)
  detachEventLog = attachEventLog(el)

  const uid = user.value?.uid
  if (!uid) {
    // The router guard should have kept us out — but if we're here, treat it
    // as an auth loss and route back to sign-in.
    await router.replace('/')
    return
  }

  const { profiles, defaultProfile: def } = await loadMyProfiles(uid)
  myProfiles.value     = profiles
  defaultProfile.value = def

  const cached = (() => {
    try { return sessionStorage.getItem(SELECTED_PROFILE_KEY) } catch { return null }
  })()

  const resolved = resolveProfileId(profiles, cached)
  if (resolved === 'need-pick') {
    // fetchAndInitialize runs inside the picker's onSelect while the dialog
    // shows "Loading…" on its primary button.
    requestProfilePick(profiles, true)
    return
  }

  // Persist the resolved choice for the next reload — but only when 2+ so we
  // don't waste writes for users who have exactly one option.
  if (resolved !== null && profiles.length > 1) {
    try { sessionStorage.setItem(SELECTED_PROFILE_KEY, resolved) } catch { /* ignore */ }
  }

  await fetchAndInitialize(resolved)
})

onBeforeUnmount(() => {
  detach?.()
  detachEventLog?.()
  sketch.value?.removeEventListener('sketch-license-validated', onLicenseValidated)
  sketch.value?.removeEventListener('sketch-error', onSketchError)
})
</script>

<template>
  <Shell>
    <template #header>
      <TopBar
        show-document-controls
        :document-name="io.documentName.value"
        :busy="io.busy.value || booting"
        :show-switch-profile="myProfiles.length >= 2"
        @new="io.newDocument()"
        @open="io.openDocument()"
        @save="io.saveDocument()"
        @export="onExport"
        @switch-profile="onSwitchProfile"
      />
    </template>

    <p v-if="banner" class="banner" :class="{ 'banner--fatal': fatal }" role="alert">{{ banner }}</p>
    <p v-else-if="io.error.value" class="banner" role="alert">{{ io.error.value }}</p>

    <div class="canvas">
      <cad-sketch ref="sketch" />
      <div v-if="booting" class="booting">Starting Sketcher…</div>
    </div>
  </Shell>

  <EventLogPanel />
</template>

<style scoped>
/* View-unique layout: the canvas fills remaining space in the Shell body's
   flex column; the booting overlay covers the canvas until the engine
   finishes initialising. Neither belongs in a shared primitive. */
.canvas {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
}

.booting {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: var(--bg);
  color: var(--fg-muted);
  font-size: 0.9rem;
}
</style>
